import { useQuery } from '@apollo/react-hooks';
import { useContext } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ConfluenceEdition } from '@confluence/change-edition';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	isTrialStandardDifferentiatorsEnrolled,
	isTrialStandardDifferentiatorsVariation,
} from '@confluence/onboarding-helpers/entry-points/flags/experiment-edition-awareness-flags';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';

import { getTrialDaysLeftFromQuery } from '../graphql/getTrialDaysLeftFromQuery';
import { TrialDetailsQuery } from '../graphql/TrialDetailsQuery.graphql';

import { usePaymentStatus } from './usePaymentStatus';

const ADMINS_FEATURE_GATE = 'confluence_standard_differentiators_all_admins_fg';

export const useStandardDifferentiatorsExperiment = () => {
	//
	// isInTrial
	//
	const isEnrolled = isTrialStandardDifferentiatorsEnrolled();
	const { edition, isLoggedIn } = useSessionData();
	const {
		data: trialData,
		error: trialError,
		loading: trialLoading,
	} = useQuery(TrialDetailsQuery, {
		skip: !(
			isEnrolled &&
			// As the experiement name suggests, Standard Differentiators is for
			// Confluence Standard only:
			edition === ConfluenceEdition.STANDARD &&
			// The GraphQL field `license` does not allow querying by anonymous. More
			// importantly, anonymous cannot make payments, so they cannot possibly
			// qualify for this experiment:
			isLoggedIn
		),
	});
	const daysLeft = !trialError && !trialLoading ? getTrialDaysLeftFromQuery(trialData) : 0;
	const isInTrial = daysLeft ? daysLeft > 0 : false;

	//
	// isQualified
	//
	const isAdminFlagEnabled = FeatureGates.checkGate(ADMINS_FEATURE_GATE);
	const makePaymentStatusQuery = isInTrial && isAdminFlagEnabled;
	const { canPay } = usePaymentStatus(!makePaymentStatusQuery);
	const { isSiteAdmin } = useContext(SPAViewContext);
	const isQualified = isInTrial && (isAdminFlagEnabled ? canPay : isSiteAdmin);

	//
	// error
	//
	let error: typeof trialError;
	if (trialError) {
		// Not all users are permitted to query the GraphQL field `license` e.g.
		// guests. Ideally, such users would be added to the `skip` condition of
		// the query above so that the query isn't executed when it's clear already
		// that it's not necessary. For example, anonymous cannot possibly make
		// payments because there is no associated id and that's why they are in the
		// `skip` condition above. But here at front-end we shouldn't presume what
		// permissions guests have, it's more maintainable to leave that to
		// back-end.
		if (isUnauthorizedError(trialError)) markErrorAsHandled(trialError);
		else error = trialError;
	}

	return {
		isQualified,
		isVariation: isTrialStandardDifferentiatorsVariation(),
		error,
		loading: trialLoading,
	};
};
